import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      retryCount: 0 
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    this.setState({ errorInfo });
    
    // Report to error tracking service if available
    if (window.gtag) {
      window.gtag('event', 'exception', {
        description: error.toString(),
        fatal: false
      });
    }
  }

  handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1
    }));
  };

  handleRefresh = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const { fallback: FallbackComponent } = this.props;
      
      // If a custom fallback component is provided, use it
      if (FallbackComponent) {
        return (
          <FallbackComponent 
            error={this.state.error}
            errorInfo={this.state.errorInfo}
            onRetry={this.handleRetry}
            onRefresh={this.handleRefresh}
          />
        );
      }

      // Default error UI
      return (
        <div style={{ 
          padding: '40px 20px', 
          minHeight: '50vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          textAlign: 'center',
          background: '#ffffff'
        }}>
          <div style={{ maxWidth: '600px', width: '100%' }}>
            <div style={{ marginBottom: '24px', fontSize: '48px', fontWeight: 'bold', color: '#000000' }}>
              ×
            </div>
            
            <h2 style={{ 
              margin: '0 0 16px 0', 
              fontSize: '24px', 
              color: '#000000',
              fontWeight: '600'
            }}>
              Oops! Something went wrong in your gallery
            </h2>
            
            <p style={{ 
              margin: '0 0 24px 0', 
              color: '#333333', 
              fontSize: '16px',
              lineHeight: '1.6'
            }}>
              We encountered an unexpected error while displaying your gallery. 
              Don't worry - your data is safe and we're working to fix this.
            </p>

            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <button 
                onClick={this.handleRetry}
                style={{
                  padding: '12px 24px',
                  background: '#000000',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'background 0.2s ease'
                }}
                onMouseOver={(e) => e.target.style.background = '#333333'}
                onMouseOut={(e) => e.target.style.background = '#000000'}
              >
                Try Again
              </button>
              
              <button 
                onClick={this.handleRefresh}
                style={{
                  padding: '12px 24px',
                  background: '#ffffff',
                  color: '#000000',
                  border: '1px solid #000000',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'background 0.2s ease'
                }}
                onMouseOver={(e) => { e.target.style.background = '#f8f9fa'; }}
                onMouseOut={(e) => { e.target.style.background = '#ffffff'; }}
              >
                Refresh Page
              </button>
            </div>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details style={{ 
                marginTop: '24px', 
                textAlign: 'left',
                background: '#f8f9fa',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #cccccc'
              }}>
                <summary style={{ 
                  cursor: 'pointer', 
                  fontWeight: '500',
                  marginBottom: '8px',
                  color: '#000000'
                }}>
                  Development Error Details
                </summary>
                <pre style={{ 
                  fontSize: '12px', 
                  overflow: 'auto',
                  color: '#333333',
                  margin: 0,
                  whiteSpace: 'pre-wrap'
                }}>
                  {this.state.error && this.state.error.toString()}
                  {this.state.errorInfo && this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;